import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import {
  sendVerificationEmail,
  validateVerificationToken,
} from '@/actions/authActions'
import { useAppDispatch } from '@/utils/typeHelpers'
import { ImageBackgroundColors } from '@/styles'
import { Button, Dialog } from '@/ui'

export const VerificationDialog = ({ isVerified }: { isVerified: boolean }) => {
  const { token } = useParams()
  const dispatch = useAppDispatch()
  const [reverifySent, setReverifySent] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [successfullyVerified, setSuccessfullyVerified] = useState<boolean>()

  useEffect(() => {
    const checkToken = async () => {
      if (token && !isVerified) {
        const res = await validateVerificationToken(token)(dispatch)
        setSuccessfullyVerified(res)
        setIsOpen(true)
      }
    }

    checkToken()
  }, [dispatch, token, isVerified])

  const resendEmail = async () => {
    // Resend then redirect to accounts page
    await sendVerificationEmail()(dispatch)
    setReverifySent(true)
  }

  const variant = useMemo(() => {
    if (reverifySent) {
      return {
        title: 'Email sent',
        text: 'We just sent you a new verification email! Click the link in your email to verify.',
        illustrationSrc:
          'https://heard-images.s3.amazonaws.com/assets/paper-plane-2.svg',
        illustrationAlt: 'Email sent illustration',
        illustrationColor: ImageBackgroundColors.YELLOW,
      }
    } else if (successfullyVerified) {
      return {
        title: 'Email verified',
        text: 'Thank you for verifying your email, and keeping your account secure.',
        illustrationSrc:
          'https://heard-images.s3.amazonaws.com/assets/check.svg',
        illustrationAlt: 'Email verified illustration',
        illustrationColor: ImageBackgroundColors.GREEN,
      }
    } else if (successfullyVerified === false) {
      return {
        title: 'Resend verification email',
        text: 'It looks like this verification link has expired! Please request a new one by clicking Send below.',
        illustrationSrc:
          'https://heard-images.s3.amazonaws.com/assets/clock.svg',
        illustrationAlt: 'Expired link illustration',
        illustrationColor: ImageBackgroundColors.ORANGE,
      }
    }

    return {
      title: 'Resend verification email',
      text: "Didn't get your verification email? Resend to verify and secure your account.",
      illustrationSrc:
        'https://heard-images.s3.amazonaws.com/assets/progress.svg',
      illustrationAlt: 'Resend verification email illustration',
      illustrationColor: ImageBackgroundColors.BLUE,
    }
  }, [successfullyVerified, reverifySent])

  return (
    <Dialog open={isOpen}>
      {!isVerified && (
        <Dialog.Trigger asChild>
          <Button variant="link" size="sm" onClick={() => setIsOpen(true)}>
            Resend verification email
          </Button>
        </Dialog.Trigger>
      )}

      <Dialog.Content
        size="sm"
        heading={variant.title}
        description={variant.text}
        illustrationSrc={variant.illustrationSrc}
        illustrationAlt={variant.illustrationAlt}
        illustrationColor={variant.illustrationColor}
      >
        <Dialog.Footer>
          <Button variant="ghost" onClick={() => setIsOpen(false)}>
            {successfullyVerified || reverifySent ? 'Close' : 'Cancel'}
          </Button>

          {!successfullyVerified && !reverifySent && (
            <Button onClick={resendEmail}>Send</Button>
          )}
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}
